/**
 * Shopify Predictive Search (autocomplete dropdown).
 *
 * Wrapper Webflow natif :
 *   <smootify-search class="smootify-search without-initial-state">
 *     <div class="dropdown-4 w-dropdown">
 *       <div class="dropdown-toggle">
 *         <form action="/search">
 *           <input name="query" class="search-input">
 *         </form>
 *       </div>
 *       <nav class="search-results-list w-dropdown-list">
 *         <div class="search-results-wrapper">
 *           <div class="search-product-wrap">                ← container
 *             <div class="div-block-27"><a class="link-3" href="/all-products">…</a></div>
 *             <search-result class="search-result">          ← template à cloner
 *               <a search="url" class="search-item">
 *                 <img search="image">
 *                 <div search="title">…</div>
 *                 <div search="vendor" class="hide">…</div>
 *                 <smootify-price>
 *                   <span data-prop="compareAtPrice" class="hide">…</span>
 *                   <span data-prop="price">…</span>
 *                 </smootify-price>
 *               </a>
 *             </search-result>
 *           </div>
 *         </div>
 *       </nav>
 *     </div>
 *   </smootify-search>
 *
 * Flow :
 *   1. `input` event debounce 200ms → fetch `predictiveSearch(query)`
 *   2. Clone le template `<search-result>` N fois
 *   3. Remplit [search="title"|"image"|"url"|"vendor"] + [data-prop="price"]
 *   4. Met à jour le lien "Accéder à la page de recherche" (href = /search?query=…)
 *   5. Ouvre le dropdown (aria-expanded + class .w--open) si query >= 2 chars
 */

import { storefront } from "../storefront/client.js";
import { PREDICTIVE_SEARCH } from "../storefront/queries.js";
import { config } from "../config.js";

// ---------------------------------------------------------------------------
//  Types
// ---------------------------------------------------------------------------

interface PredictiveProduct {
  id: string;
  handle: string;
  title: string;
  vendor: string;
  featuredImage: { url: string; altText: string } | null;
  priceRange: {
    minVariantPrice: { amount: string; currencyCode: string };
  };
}

interface PredictiveQuery {
  text: string;
  styledText: string;
}

interface PredictiveResponse {
  predictiveSearch: {
    products: PredictiveProduct[];
    queries: PredictiveQuery[];
  };
}

// ---------------------------------------------------------------------------
//  Helpers
// ---------------------------------------------------------------------------

function setText(el: Element, text: string): void {
  el.textContent = text;
}

function formatMoney(amount: string, currency: string): string {
  try {
    const n = Number.parseFloat(amount);
    if (!Number.isFinite(n)) return "";
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency,
    }).format(n);
  } catch {
    return `${amount} ${currency}`;
  }
}

function searchPageUrl(query: string): string {
  const q = encodeURIComponent(query.trim());
  return `/search?query=${q}`;
}

// ---------------------------------------------------------------------------
//  Rendering
// ---------------------------------------------------------------------------

function removePreviousResults(container: Element): void {
  container
    .querySelectorAll(':scope > [data-lmdr-search-result="true"]')
    .forEach((n) => n.remove());
}

function fillProductClone(
  clone: HTMLElement,
  product: PredictiveProduct,
): void {
  // [search="title"]
  clone.querySelectorAll<HTMLElement>('[search="title"]').forEach((el) => {
    setText(el, product.title);
  });
  // [search="vendor"] — retire la class .hide si vendor présent
  clone.querySelectorAll<HTMLElement>('[search="vendor"]').forEach((el) => {
    if (product.vendor) {
      setText(el, product.vendor);
      el.classList.remove("hide");
      el.style.removeProperty("display");
    } else {
      setText(el, "");
      el.classList.add("hide");
    }
  });
  // [search="image"]
  clone.querySelectorAll<HTMLImageElement>('img[search="image"]').forEach((img) => {
    const url = product.featuredImage?.url;
    if (url) {
      img.src = url;
      if (product.featuredImage?.altText) img.alt = product.featuredImage.altText;
      img.removeAttribute("srcset");
    }
  });
  // [search="url"] (lien vers la fiche produit)
  clone.querySelectorAll<HTMLElement>('[search="url"]').forEach((el) => {
    const base = config.productsBase.replace(/^\/+|\/+$/g, "");
    const href = `/${base}/${product.handle}`;
    if (el instanceof HTMLAnchorElement) {
      el.href = href;
    } else {
      el.setAttribute("href", href);
    }
  });
  // [data-prop="price"] / [data-prop="compareAtPrice"] (smootify-price children)
  const price = product.priceRange?.minVariantPrice;
  if (price) {
    clone.querySelectorAll<HTMLElement>('[data-prop="price"]').forEach((el) => {
      setText(el, formatMoney(price.amount, price.currencyCode));
    });
  }
  // compareAtPrice : pas dans la query predictive → on cache le span
  clone
    .querySelectorAll<HTMLElement>('[data-prop="compareAtPrice"]')
    .forEach((el) => {
      el.classList.add("hide");
    });
  // [search="price"] (legacy fallback)
  clone.querySelectorAll<HTMLElement>('[search="price"]').forEach((el) => {
    if (price) setText(el, formatMoney(price.amount, price.currencyCode));
  });
}

function fillSuggestedQueryClone(
  clone: HTMLElement,
  query: PredictiveQuery,
): void {
  // Si c'est un anchor, on définit href + texte
  if (clone instanceof HTMLAnchorElement) {
    clone.href = searchPageUrl(query.text);
  }
  // On remplit le texte (textContent ou un enfant [search="suggested-query-text"])
  const textTarget =
    clone.querySelector<HTMLElement>('[search="suggested-query-text"]') ?? clone;
  setText(textTarget, query.text);
}

// ---------------------------------------------------------------------------
//  State detection
// ---------------------------------------------------------------------------

interface PredictiveSearchState {
  wrapper: HTMLElement;
  input: HTMLInputElement;
  template: HTMLElement;
  container: HTMLElement;
  searchPageLink: HTMLAnchorElement | null;
  suggestionsTemplate: HTMLElement | null;
  suggestionsContainer: HTMLElement | null;
}

function findState(): PredictiveSearchState | null {
  if (typeof document === "undefined") return null;

  // Wrapper natif Smootify Webflow.
  const wrapper = document.querySelector<HTMLElement>("smootify-search");
  if (!wrapper) return null;

  // Template à cloner : premier <search-result> dans le wrapper.
  // Fallback : un élément qui contient [search="url"] ou [search="title"].
  let template = wrapper.querySelector<HTMLElement>("search-result");
  if (!template) {
    // Fallback : on prend le parent commun de [search="url"] (le "search-item")
    const urlEl = wrapper.querySelector<HTMLElement>('[search="url"]');
    template = (urlEl?.closest<HTMLElement>("search-result, .search-item")) ?? urlEl;
  }
  if (!template) return null;

  const container = template.parentElement as HTMLElement | null;
  if (!container) return null;

  // Input : scopé au wrapper.
  const input =
    wrapper.querySelector<HTMLInputElement>('input[search="input"]') ??
    wrapper.querySelector<HTMLInputElement>('input[name="query"]') ??
    wrapper.querySelector<HTMLInputElement>("input.search-input");
  if (!input) return null;

  // Lien "Accéder à la page de recherche" :
  //   - explicitement [search="search-page"]
  //   - sinon a.link-3 (convention Webflow LMDR)
  //   - sinon premier a[href^="/search"] ou a[href^="/all-products"]
  const searchPageLink =
    wrapper.querySelector<HTMLAnchorElement>('a[search="search-page"]') ??
    wrapper.querySelector<HTMLAnchorElement>("a.link-3") ??
    wrapper.querySelector<HTMLAnchorElement>('a[href^="/search"]') ??
    wrapper.querySelector<HTMLAnchorElement>('a[href^="/all-products"]');

  // Suggestions (optionnel)
  const suggestionsTemplate = wrapper.querySelector<HTMLElement>(
    '[search="suggested-query"]',
  );
  const suggestionsContainer =
    (suggestionsTemplate?.parentElement as HTMLElement | null) ?? null;

  return {
    wrapper,
    input,
    template,
    container,
    searchPageLink,
    suggestionsTemplate,
    suggestionsContainer,
  };
}

// ---------------------------------------------------------------------------
//  Main
// ---------------------------------------------------------------------------

let currentController: AbortController | null = null;
let debounceTimer: number | undefined;

function setOpenState(wrapper: HTMLElement, hasResults: boolean): void {
  // Smootify natif utilise `without-initial-state` / `with-initial-state`.
  if (hasResults) {
    wrapper.classList.remove("without-initial-state");
    wrapper.classList.add("with-initial-state");
  } else {
    wrapper.classList.add("without-initial-state");
    wrapper.classList.remove("with-initial-state");
  }
}

async function runPredictiveSearch(state: PredictiveSearchState): Promise<void> {
  const query = state.input.value.trim();

  // Lien "search-page" maintenu en live, même pour query courte.
  if (state.searchPageLink) {
    state.searchPageLink.href = searchPageUrl(query);
  }

  if (query.length < 2) {
    removePreviousResults(state.container);
    if (state.suggestionsContainer) {
      removePreviousResults(state.suggestionsContainer);
    }
    setOpenState(state.wrapper, false);
    return;
  }

  // Annule la requête en cours si besoin.
  currentController?.abort();
  currentController = new AbortController();

  try {
    const data = await storefront.query<PredictiveResponse>(
      PREDICTIVE_SEARCH,
      { query, limit: 10 },
      { cache: false },
    );
    const result = data.predictiveSearch;

    // Produits : cache le template + insère N clones.
    state.template.style.display = "none";
    state.template.setAttribute("data-lmdr-multi-template", "true");
    removePreviousResults(state.container);

    const products = result.products ?? [];
    products.forEach((p) => {
      const clone = state.template.cloneNode(true) as HTMLElement;
      clone.removeAttribute("data-lmdr-multi-template");
      clone.setAttribute("data-lmdr-search-result", "true");
      clone.style.removeProperty("display");
      fillProductClone(clone, p);
      state.container.appendChild(clone);
    });

    // Suggestions (si template présent dans le DOM)
    if (state.suggestionsTemplate && state.suggestionsContainer) {
      state.suggestionsTemplate.style.display = "none";
      state.suggestionsTemplate.setAttribute("data-lmdr-multi-template", "true");
      removePreviousResults(state.suggestionsContainer);
      (result.queries ?? []).forEach((q) => {
        const clone = state.suggestionsTemplate!.cloneNode(true) as HTMLElement;
        clone.removeAttribute("data-lmdr-multi-template");
        clone.setAttribute("data-lmdr-search-result", "true");
        clone.style.removeProperty("display");
        fillSuggestedQueryClone(clone, q);
        state.suggestionsContainer!.appendChild(clone);
      });
    }

    setOpenState(state.wrapper, products.length > 0);
  } catch (err) {
    if ((err as DOMException)?.name !== "AbortError") {
      // eslint-disable-next-line no-console
      console.warn("[lmdr] predictive search failed:", err);
    }
  }
}

export function attachPredictiveSearch(): void {
  const state = findState();
  if (!state) return;

  if (state.input.hasAttribute("data-lmdr-pred-bound")) return;
  state.input.setAttribute("data-lmdr-pred-bound", "true");

  state.input.addEventListener("input", () => {
    if (debounceTimer !== undefined) window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      void runPredictiveSearch(state);
    }, 200);
  });

  // Au submit du form contenant cet input, redirige vers /search?query=...
  const form = state.input.closest("form");
  if (form && !form.hasAttribute("data-lmdr-pred-form-bound")) {
    form.setAttribute("data-lmdr-pred-form-bound", "true");
    form.addEventListener(
      "submit",
      (ev) => {
        const q = state.input.value.trim();
        if (!q) {
          ev.preventDefault();
          return;
        }
        ev.preventDefault();
        ev.stopPropagation();
        window.location.href = searchPageUrl(q);
      },
      true,
    );
  }
}
